import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { FileCheck, ShieldCheck } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useVerificationRecords } from "@/hooks/useVerification";
import { formatDate } from "@/utils/formatters";

export const Route = createFileRoute("/student/verification/")({
  component: StudentVerificationPage,
});

function StudentVerificationPage() {
  const { data = [], isLoading } = useVerificationRecords();
  const [selected, setSelected] = useState<any | null>(null);
  const records = useMemo(() => (data as any[]) || [], [data]);
  const verified = useMemo(() => records.filter((r) => r.status === "verified").length, [records]);
  if (isLoading)
    return <TableSkeleton rows={5} columns={4} />;
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Verification records"
        subtitle={`${verified} of ${records.length} records verified.`}
      />
      <ul className="divide-y divide-border overflow-hidden rounded-md border border-border bg-card">
        {records.map((r, i) => (
          <li
            key={r.id || i}
            className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between md:px-5"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body"
                aria-hidden
              >
                <ShieldCheck className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {r.placement_title || r.title || "Verification record"}
                </p>
                <p className="text-caption text-muted-foreground">
                  {r.partner_name || r.verified_by || "Partner"} •{" "}
                  {formatDate(r.created_at || r.start_date)}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <Badge
                dot
                className="capitalize"
                variant={r.status === "verified" ? "success" : "warning"}
              >
                {r.status || "pending"}
              </Badge>
              <Button variant="outline" size="sm" onClick={() => setSelected(r)}>
                View
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="size-4 text-muted-foreground" aria-hidden />
              Verification detail
            </DialogTitle>
            <DialogDescription>
              {selected?.placement_title || selected?.title || "Verification record"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
